import moment from "moment";
import { formatRupees } from "../SalaryComponents/salaryComponentData";
import {
  PERIOD,
  compareLines,
  currentTemplateVersion,
  evaluateTemplate,
  lineCategory,
  lineErrors,
  versionLabel,
  withBalancing,
} from "../SalaryTemplates/salaryTemplateData";

// Helpers for Assign Salary (data comes from assignSalaryApi.js). Salary
// Revision also uses APPROVAL_LEVELS, requestStatus, effectiveRange and
// formatStamp, so those keep working on its sample requests too.

export const APPROVAL_LEVELS = [
  { level: 1, role: "HR Manager" },
  { level: 2, role: "Finance Manager" },
];

// Used until get-salary-assign-choices answers.
export const SALARY_TYPES = ["Monthly", "Weekly", "Daily"];

const today = () => moment().startOf("day");
export const formatDay = (value) => (value ? moment(value).format("DD MMM YYYY") : "–");
export const formatStamp = (value) => (value ? moment(value).format("DD MMM YYYY, HH:mm") : "–");

// Default Effective from: the 1st of the current month, or the joining date
// when the employee joined later than that (it can't be before joining).
export const defaultEffectiveFrom = (joined) => {
  const monthStart = moment().startOf("month");
  return joined && moment(joined).isAfter(monthStart, "day") ? moment(joined).format("YYYY-MM-DD") : monthStart.format("YYYY-MM-DD");
};

const sameId = (a, b) => a != null && b != null && String(a) === String(b);
export const employeeById = (employees, id) => employees.find((employee) => sameId(employee.id, id));

export const waitingDays = (employee) =>
  employee.waiting != null ? employee.waiting : employee.joined ? Math.max(0, today().diff(moment(employee.joined), "days")) : null;

// ---------- Requests ----------

export const requestTemplate = (templates, request) => templates.find((template) => sameId(template.id, request.template_id));

// The request's template version, else the loaded current version.
export const requestTemplateVersion = (templates, request) => {
  const template = requestTemplate(templates, request);
  if (!template) return null;
  return template.versions.find((version) => version.version === request.template_version && version.lines?.length) || currentTemplateVersion(template);
};

export const employeeLines = (templates, request, row) =>
  row.lines || row.structure_lines || requestTemplateVersion(templates, request)?.lines || [];

const startDate = (request) => {
  const dates = (request.employees || []).map((row) => row.effective_from).filter(Boolean);
  if (!dates.length && request.effective_from) dates.push(request.effective_from);
  return dates.length ? moment.min(dates.map((value) => moment(value))) : null;
};

// Status shown on the list: label, tone and the line under it.
export const requestStatus = (request) => {
  if (request.status === "Pending") {
    const level = APPROVAL_LEVELS.find((item) => item.level === request.level);
    return { key: "Pending", label: "Pending approval", short: `Pending L${request.level}`, tone: "amber", detail: `Waiting at L${request.level} · ${level?.role || "Approver"}` };
  }
  if (request.status === "Draft") {
    const parts = [request.saved_by ? `Saved by ${request.saved_by}` : "Not submitted", request.saved_at ? moment(request.saved_at).format("DD MMM") : null];
    return { key: "Draft", label: "Draft", short: "Draft", tone: "slate", detail: parts.filter(Boolean).join(" · ") };
  }
  if (request.status === "Rejected") {
    const reason = String(request.rejection?.reason || "").split(/[.(]/)[0].trim();
    return { key: "Rejected", label: "Rejected", short: "Rejected", tone: "red", detail: [request.rejection?.role, reason].filter(Boolean).join(" · ") };
  }
  if (request.status === "Cancelled") {
    return { key: "Cancelled", label: "Cancelled", short: "Cancelled", tone: "slate", detail: "Withdrawn before final approval" };
  }
  const start = startDate(request);
  const upcoming = request.upcoming ?? Boolean(start && start.isAfter(today()));
  const approved = request.approved_at ? `Approved ${moment(request.approved_at).format("DD MMM")}` : "Approved";
  if (upcoming) {
    return { key: "Upcoming", label: "Approved · Upcoming", short: "Upcoming", tone: "green", detail: start ? `${approved} · starts ${start.format("DD MMM")}` : approved };
  }
  return { key: "Applied", label: "Applied", short: "Applied", tone: "slate", detail: start ? `Applied in ${start.format("MMM")} payroll` : approved };
};

export const effectiveRange = (request) => {
  const dates = (request.employees || []).map((row) => row.effective_from).filter(Boolean).map((value) => moment(value));
  if (!dates.length) {
    if (request.effective_from) dates.push(moment(request.effective_from));
    if (request.effective_to) dates.push(moment(request.effective_to));
  }
  if (!dates.length) return "–";
  dates.sort((a, b) => a - b);
  const from = dates[0];
  const to = dates[dates.length - 1];
  if (from.isSame(to, "day")) return from.format("DD MMM YYYY");
  if (from.isSame(to, "month")) return `${from.format("DD")}–${to.format("DD MMM YYYY")}`;
  return `${from.format("DD MMM")} – ${to.format("DD MMM YYYY")}`;
};

// List rows name the employees without their gross, so the request's own
// total is used until the employees' amounts are loaded.
const hasGross = (request) => request.employees?.some((row) => Number(row.gross) > 0);

export const totalGross = (request) =>
  hasGross(request)
    ? request.employees.reduce((total, row) => total + Number(row.gross || 0), 0)
    : request.total_gross ?? (request.gross != null ? request.gross * (request.employee_count || 1) : 0);

export const employeeCount = (request) => request.employees?.length || request.employee_count || 0;

// What a customized employee changes from the template version.
export const structureChanges = (catalog, templates, request, row) => {
  if (!row.lines) return [];
  const base = requestTemplateVersion(templates, request)?.lines || [];
  if (!base.length) return [];
  return compareLines(catalog, base, row.lines).filter((item) => item.change !== "Unchanged");
};

export const isCustomized = (catalog, templates, request, row) =>
  Boolean(row.customized || structureChanges(catalog, templates, request, row).length);

export const customizedCount = (catalog, templates, request) =>
  request.employees?.length
    ? request.employees.filter((row) => isCustomized(catalog, templates, request, row)).length
    : request.customized_count || 0;

// The flat row the requests table and CustomFilter work on.
export const toRequestRow = (request) => {
  const status = requestStatus(request);
  const count = employeeCount(request);
  const names = request.employees?.length ? request.employees.map((row) => row.name).filter(Boolean) : request.employee_names || [];
  const single = count === 1 ? request.employees?.[0] || { name: names[0] || "" } : null;
  const customized = request.employees?.length ? request.employees.filter((row) => row.customized).length : request.customized_count || 0;
  const template = request.template_name || "Template";
  const version = versionLabel(request.template_version);
  const grosses = hasGross(request) ? request.employees.map((row) => Number(row.gross || 0)) : [];
  const sameGross = count > 1 && grosses.length === count && grosses.every((value) => value === grosses[0]);
  return {
    id: request.id,
    request: request.title || (single?.name ? single.name : `${template} · ${count} employee${count === 1 ? "" : "s"}`),
    request_detail: [
      single?.name || request.type === "Import" ? `${template} ${version}` : `Template ${version}`,
      request.type === "Import" ? `${count} employees` : null,
      customized ? `${customized} customized` : null,
      request.status === "Draft" ? "not submitted" : request.number,
    ].filter(Boolean).join(" · "),
    number: request.number,
    type: request.type,
    template,
    payroll_group: `${request.payroll_group || "–"} · ${request.salary_type}`,
    group_detail: request.type === "Import" && request.imported_at
      ? `Imported ${moment(request.imported_at).format("DD MMM")}`
      : single
        ? [single.position, single.department].filter(Boolean).join(" · ") || `${count} employee`
        : names.length ? names.join(", ") : `${count} employees`,
    employees: count,
    names: names.join(" "),
    gross: totalGross(request),
    period: PERIOD[request.salary_type] || "month",
    each: sameGross ? grosses[0] : count > 1 && !grosses.length && request.gross != null && request.total_gross == null ? request.gross : null,
    effective: effectiveRange(request),
    status: status.label,
    status_key: status.key,
    status_tone: status.tone,
    status_detail: status.detail,
    mine: Boolean(request.pending_mine),
  };
};

// ---------- Assignment form ----------

export const emptyAssignForm = (people = [], groups = []) => {
  const group = groups.find((item) => people[0]?.group && item.name === people[0].group);
  return {
    request_id: null,
    payroll_group_id: group?.id ?? null,
    salary_type: "Monthly",
    template_id: null,
    gross_mode: "same",
    same_gross: "",
    comments: "",
    employees: people.map((person) => ({ employee_id: person.id, gross: "", effective_from: defaultEffectiveFrom(person.joined), lines: null })),
  };
};

// Opens on "Same gross for all" unless the saved employees have different
// amounts, so a mixed request isn't overwritten.
export const requestToForm = (request) => {
  const employees = request.employees.map((row) => ({
    employee_id: row.employee_id,
    gross: row.gross === "" || row.gross == null ? "" : String(row.gross),
    effective_from: row.effective_from || defaultEffectiveFrom(row.joined),
    lines: row.lines ? row.lines.map((line) => ({ ...line })) : null,
  }));
  const amounts = [...new Set(employees.map((row) => row.gross))];
  const same = amounts.length <= 1;
  return {
    request_id: request.id,
    payroll_group_id: request.payroll_group_id,
    salary_type: request.salary_type,
    template_id: request.template_id,
    gross_mode: same ? "same" : "per",
    same_gross: same ? amounts[0] || "" : "",
    comments: request.comments || "",
    employees,
  };
};

// The gross a customized structure adds up to: Earnings worked out again at
// their own total until it stops moving (rows on GROSS move with it).
// Returns the gross, or null when the rows can't settle on one.
export const grossForLines = (catalog, lines, gross) => {
  let current = gross > 0 ? gross : 0;
  for (let pass = 0; pass < 200; pass += 1) {
    const result = evaluateTemplate(catalog, lines, current);
    if (result.unresolved.length || !(result.earnings > 0)) return null;
    if (result.balanced) {
      // Prefer the whole-rupee gross when it adds up too (49,999.87 -> 50,000).
      const whole = Math.round(current);
      return evaluateTemplate(catalog, lines, whole).balanced ? whole : Math.round(current * 100) / 100;
    }
    // Close to a whole rupee: check it directly before another pass.
    const whole = Math.round(result.earnings);
    if (Math.abs(whole - result.earnings) < 0.5 && evaluateTemplate(catalog, lines, whole).balanced) return whole;
    current = result.earnings;
  }
  return null;
};

// A gross other than the template's own: the template's Fixed Amount
// earnings are scaled so Earnings add up to the new gross (Percentage and
// Formula rows follow by themselves). Returns the scaled lines, or null when
// the lines already add up or can't be made to (no fixed earning, or the
// other rows alone are above the gross).
const round2 = (value) => Math.round(value * 100) / 100;
export const scaleLinesToGross = (catalog, lines, gross) => {
  if (!lines?.length || !(gross > 0)) return null;
  const start = evaluateTemplate(catalog, lines, gross);
  if (start.balanced || start.unresolved.length) return null;
  const fixed = lines.filter((line) => !line.balancing && line.method === "Fixed Amount" && lineCategory(catalog, line) === "Earnings");
  if (!fixed.length) return null;
  const codes = new Set(fixed.map((line) => line.code));
  const total = fixed.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
  // Fixed earnings set to factor x their share; earnings move in a straight
  // line with the factor (percentages / formulas on them included).
  const at = (factor) =>
    lines.map((line) =>
      codes.has(line.code)
        ? { ...line, amount: round2(total > 0 ? (Number(line.amount) || 0) * factor : line === fixed[0] ? factor : 0) }
        : line,
    );
  const earningsAt = (factor) => evaluateTemplate(catalog, at(factor), gross).earnings;
  const unit = total > 0 ? 1 : gross;
  const zero = earningsAt(0);
  const slope = earningsAt(unit) - zero;
  if (!(slope > 0) || zero >= gross) return null;
  let scaled = at(((gross - zero) / slope) * unit);
  // Rounding left-over goes on the biggest fixed earning.
  const biggest = [...fixed].sort((a, b) => (Number(b.amount) || 0) - (Number(a.amount) || 0))[0].code;
  for (let pass = 0; pass < 3; pass += 1) {
    const result = evaluateTemplate(catalog, scaled, gross);
    const diff = round2(gross - result.earnings);
    if (Math.abs(diff) < 0.01) break;
    scaled = scaled.map((line) => (line.code === biggest ? { ...line, amount: round2(Number(line.amount) + diff) } : line));
  }
  const result = evaluateTemplate(catalog, scaled, gross);
  return result.balanced && scaled.every((line) => !codes.has(line.code) || Number(line.amount) >= 0) ? scaled : null;
};

// Checks for the whole form. Returns { errors, perEmployee, earningsOk,
// linesReady } — errors block submit; drafts only need a group and a
// template. The server repeats every check (no salary yet, not in another
// open request, payroll not processed for the date).
export const checkAssignForm = (form, { catalog, template, employees }) => {
  const errors = {};
  const perEmployee = {};
  const version = template ? currentTemplateVersion(template) : null;
  const linesReady = Boolean(version?.lines?.length);
  if (!form.payroll_group_id) errors.payroll_group = "Select a payroll group";
  if (!form.template_id) errors.template = "Select a template";
  if (!form.employees.length) errors.employees = "Add at least one employee";
  let earningsOk = linesReady;
  form.employees.forEach((row) => {
    const employee = employeeById(employees, row.employee_id);
    const problems = [];
    const gross = Number(row.gross);
    if (!(gross > 0)) problems.push("Enter a gross above 0");
    if (!row.effective_from) problems.push("Pick an effective date");
    else if (employee?.joined && moment(row.effective_from).isBefore(employee.joined, "day")) problems.push(`Can't be before joining (${formatDay(employee.joined)})`);
    if (linesReady && gross > 0) {
      const lines = row.lines || version.lines;
      const result = evaluateTemplate(catalog, lines, gross);
      if (!result.balanced || result.unresolved.length || Object.keys(lineErrors(catalog, lines)).length) {
        earningsOk = false;
        problems.push("Earnings don't add up to gross");
      }
    }
    if (problems.length) perEmployee[row.employee_id] = problems;
  });
  return { errors, perEmployee, earningsOk, linesReady, template, version };
};

export { evaluateTemplate, withBalancing, versionLabel, PERIOD, formatRupees };
